import {isAxiosError} from 'axios';
import type {AxiosInstance, AxiosResponse} from 'axios';
import type ErgAuthService from '@ergeon/erg-auth';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const TOKEN_CHECK_TIMEOUT_MS = 10_000;

type SrvTokenVerdict = 'accepted' | 'rejected' | 'no-verdict';

export const hasLiveSession = (authService: ErgAuthService): boolean => {
  const payload = authService.getPayload(authService.getToken());
  if (!payload) {
    return false;
  }

  return !authService.isExpired(payload);
};

const abortAfter = (milliseconds: number): AbortSignal => {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), milliseconds);
  return controller.signal;
};

const userDetailsRequest = (authService: ErgAuthService): RequestInit => {
  const token = authService.getToken();
  return {
    method: 'POST',
    headers: {
      Authorization: authService.getAuthHeader(token),
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({token}),
    signal: abortAfter(TOKEN_CHECK_TIMEOUT_MS),
  };
};

const refusesToken = (status: number): boolean => status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN;

const verdictOf = (response: Response): SrvTokenVerdict => {
  if (response.ok) {
    return 'accepted';
  }

  if (refusesToken(response.status)) {
    return 'rejected';
  }

  return 'no-verdict';
};

export const askSrvAboutCurrentToken = async (authService: ErgAuthService): Promise<SrvTokenVerdict> => {
  const {userDetailsEndpoint} = authService.options;
  if (!userDetailsEndpoint) {
    return 'no-verdict';
  }

  try {
    const response = await fetch(userDetailsEndpoint, userDetailsRequest(authService));
    return verdictOf(response);
  } catch {
    return 'no-verdict';
  }
};

const isUnauthorized = (response?: AxiosResponse): boolean => response?.status === HTTP_UNAUTHORIZED;

export const watchUnauthorizedResponses = (client: AxiosInstance, onUnauthorized: () => void): (() => void) => {
  const interceptorId = client.interceptors.response.use(
    (response) => {
      if (isUnauthorized(response)) {
        onUnauthorized();
      }
      return response;
    },
    (error: unknown) => {
      if (isAxiosError(error) && isUnauthorized(error.response)) {
        onUnauthorized();
      }
      return Promise.reject(error);
    },
  );

  return () => client.interceptors.response.eject(interceptorId);
};
